import React, { useEffect, useState, useMemo } from "react";
import {
    Alert, Button, Card, CardBody, Col, Container, FormGroup, Label,
    Modal, ModalBody, ModalHeader, Row, Spinner, Table
} from "reactstrap";
import { useNavigate } from "react-router-dom";
import Session from "../../auth/Session";
import { Roles } from "../../auth/Roles";
import useSortableData from "../useSortableData";
import SortableHeader from "../SortableHeader";

const fmt = (d) => (d ? new Date(d).toLocaleString() : "");
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : "");
const money = (n) => (n ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const ApprovedPaySheetsReport = () => {
    const navigate = useNavigate();
    const [paySheets, setPaySheets] = useState([]);
    const [loading, setLoading] = useState(false);
    const [voiding, setVoiding] = useState(false);
    const [pushingId, setPushingId] = useState(null);
    const [posting, setPosting] = useState(false);
    const [error, setError] = useState("");
    const [selectedPaySheet, setSelectedPaySheet] = useState(null);
    const [qboTarget, setQboTarget] = useState(null);
    const [qboPreview, setQboPreview] = useState(null);
    const [qboPreviewOpen, setQboPreviewOpen] = useState(false);
    const [qboBlocked, setQboBlocked] = useState(false);

    const sortGetters = useMemo(() => ({
        week: (p) => (p.weekStartDate ? new Date(p.weekStartDate) : null),
        paySheetDate: (p) => (p.paySheetDate ? new Date(p.paySheetDate) : null),
        paySheetAmount: (p) => Number(p.paySheetAmount ?? 0),
        approvalDate: (p) => (p.approvalDate ? new Date(p.approvalDate) : null),
        voidedDate: (p) => (p.voidedDate ? new Date(p.voidedDate) : null),
    }), []);

    const { items: sortedPaySheets, sortConfig, requestSort } = useSortableData(
        paySheets,
        { key: "week", direction: "asc", getters: sortGetters }
    );

    const loadPaySheets = async () => {
        if (!Session.hasRole(Roles.Admin)) {
            navigate("/accessdenied");
            return;
        }

        setLoading(true);
        setError("");
        try {
            const token = await Session.getToken();
            const res = await fetch("/api/reports/paysheets", {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (!res.ok) throw new Error(`${res.status} ${await res.text()}`);
            setPaySheets(await res.json());
        } catch (e) {
            setError(e.message || "Failed to load paysheet report.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadPaySheets();
    }, []);

    const voidPaySheet = async () => {
        if (!selectedPaySheet) return;

        setVoiding(true);
        setError("");
        try {
            const token = await Session.getToken();
            const res = await fetch(`/api/reports/paysheets/${selectedPaySheet.paySheetId}/void`, {
                method: "POST",
                headers: { Authorization: `Bearer ${token}` }
            });
            if (!res.ok) throw new Error(`${res.status} ${await res.text()}`);
            setPaySheets(await res.json());
            setSelectedPaySheet(null);
        } catch (e) {
            setError(e.message || "Failed to void paysheet.");
        } finally {
            setVoiding(false);
        }
    };

    // Step 1: run the same validation the audit page uses (linked vendor and every line resolves
    // to a QuickBooks account). If it passes, show a confirmation that the data will be posted; if
    // not, show the blocked preview so the user can fix the missing links.
    const startPush = async (paySheet) => {
        setPushingId(paySheet.paySheetId);
        setError("");
        try {
            const token = await Session.getToken();
            const res = await fetch(`/api/reports/paysheets/${paySheet.paySheetId}/quickbooks-preview`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (!res.ok) throw new Error(`${await res.text()}`);
            const preview = await res.json();
            setQboTarget(paySheet);
            setQboPreview(preview);
            setQboBlocked(!preview?.isValid);
            setQboPreviewOpen(true);
        } catch (e) {
            setError(e.message || "Failed to validate paysheet for QuickBooks.");
        } finally {
            setPushingId(null);
        }
    };

    // Step 2: validation passed and the user confirmed — actually post to QuickBooks.
    const confirmPush = async () => {
        if (!qboTarget) return;
        setPosting(true);
        setError("");
        try {
            const token = await Session.getToken();
            const res = await fetch(`/api/reports/paysheets/${qboTarget.paySheetId}/push-quickbooks`, {
                method: "POST",
                headers: { Authorization: `Bearer ${token}` }
            });
            if (!res.ok) throw new Error(`${await res.text()}`);
            setPaySheets(await res.json());
            setQboPreviewOpen(false);
            setQboTarget(null);
        } catch (e) {
            setError(e.message || "Failed to push paysheet to QuickBooks.");
        } finally {
            setPosting(false);
        }
    };

    return (
        <Container className="mt-0">
            <Card>
                <CardBody>
                    <Row className="mb-3">
                        <Col><h5>Approved Paysheet Report</h5></Col>
                    </Row>

                    {error && <Alert color="danger">{error}</Alert>}

                    {loading ? (
                        <div className="text-center py-4"><Spinner /></div>
                    ) : (
                        <Table bordered hover responsive>
                            <thead className="table-light">
                                <tr>
                                    <SortableHeader label="Status" sortKey="status" sortConfig={sortConfig} onSort={requestSort} />
                                    <SortableHeader label="Week" sortKey="week" sortConfig={sortConfig} onSort={requestSort} />
                                    <SortableHeader label="Driver" sortKey="driverName" sortConfig={sortConfig} onSort={requestSort} />
                                    <SortableHeader label="Pay Sheet Date" sortKey="paySheetDate" sortConfig={sortConfig} onSort={requestSort} />
                                    <SortableHeader label="Pay Sheet Total" sortKey="paySheetAmount" sortConfig={sortConfig} onSort={requestSort} style={{ textAlign: "right" }} />
                                    <SortableHeader label="Approved By" sortKey="approvedBy" sortConfig={sortConfig} onSort={requestSort} />
                                    <SortableHeader label="Approved Date" sortKey="approvalDate" sortConfig={sortConfig} onSort={requestSort} />
                                    <SortableHeader label="Voided By" sortKey="voidedBy" sortConfig={sortConfig} onSort={requestSort} />
                                    <SortableHeader label="Voided Date" sortKey="voidedDate" sortConfig={sortConfig} onSort={requestSort} />
                                    <th>QuickBooks</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {sortedPaySheets.length === 0 ? (
                                    <tr><td colSpan="11" className="text-center">No approved paysheets found.</td></tr>
                                ) : sortedPaySheets.map(paySheet => (
                                    <tr key={paySheet.paySheetId} className={paySheet.voided ? "table-warning" : ""}>
                                        <td>{paySheet.status}</td>
                                        <td>{fmtDate(paySheet.weekStartDate)} - {fmtDate(paySheet.weekEndDate)}</td>
                                        <td>{paySheet.driverName}</td>
                                        <td>{fmtDate(paySheet.paySheetDate)}</td>
                                        <td style={{ textAlign: "right" }}>${money(paySheet.paySheetAmount)}</td>
                                        <td>{paySheet.approvedBy}</td>
                                        <td>{fmt(paySheet.approvalDate)}</td>
                                        <td>{paySheet.voidedBy}</td>
                                        <td>{fmt(paySheet.voidedDate)}</td>
                                        <td className="text-center">
                                            {paySheet.voided ? (
                                                <span className="text-muted">—</span>
                                            ) : paySheet.qbBillId ? (
                                                <span className="text-success">Synced {fmt(paySheet.qbSyncedAt)}</span>
                                            ) : (
                                                <>
                                                    <Button size="sm" color="primary" disabled={pushingId === paySheet.paySheetId} onClick={() => startPush(paySheet)}>
                                                        {pushingId === paySheet.paySheetId ? <><Spinner size="sm" /> Checking...</> : "Send to QuickBooks"}
                                                    </Button>
                                                    {paySheet.qbSyncError && <div className="text-danger small mt-1">{paySheet.qbSyncError}</div>}
                                                </>
                                            )}
                                        </td>
                                        <td className="text-center">
                                            <Button size="sm" color="secondary" outline className="me-2" onClick={() => navigate(`/reports/paysheet-view/${paySheet.paySheetId}`)}>
                                                View
                                            </Button>
                                            {!paySheet.voided && (
                                                <Button size="sm" color="warning" onClick={() => setSelectedPaySheet(paySheet)}>
                                                    Void
                                                </Button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    )}
                </CardBody>
            </Card>

            <Modal isOpen={qboPreviewOpen} toggle={() => !posting && setQboPreviewOpen(false)} size="lg">
                <ModalHeader toggle={() => !posting && setQboPreviewOpen(false)}>
                    {qboBlocked ? "Cannot Create QuickBooks Check" : "Confirm QuickBooks Check"}
                    {qboPreview?.docNumber ? ` — #${qboPreview.docNumber}` : ""}
                </ModalHeader>
                <ModalBody>
                    {qboBlocked ? (
                        <Alert color="danger" className="mb-3">
                            This pay sheet <strong>cannot be pushed</strong> because it is missing a QuickBooks
                            vendor and/or one or more lines are not linked to a QuickBooks account. Fix the
                            highlighted items, then try again.
                        </Alert>
                    ) : (
                        <Alert color="info" className="mb-3">
                            Validation passed. This pay sheet <strong>will be posted to QuickBooks</strong> as a
                            check when you confirm below.
                        </Alert>
                    )}
                    {qboPreview && (
                        <>
                            <Row className="mb-3">
                                <Col md={6}>
                                    <div className="text-muted small">Vendor</div>
                                    <div>{qboPreview.vendorName || <span className="text-danger">— not linked —</span>}</div>
                                </Col>
                                <Col md={6}>
                                    <div className="text-muted small">Check Date</div>
                                    <div>{qboPreview.txnDate || "—"}</div>
                                </Col>
                            </Row>
                            {qboPreview.warnings && qboPreview.warnings.length > 0 && (
                                <Alert color="warning">
                                    <div className="fw-bold mb-1">Issues that would affect this push:</div>
                                    <ul className="mb-0">
                                        {qboPreview.warnings.map((w, i) => <li key={i}>{w}</li>)}
                                    </ul>
                                </Alert>
                            )}
                            <Table bordered size="sm" responsive>
                                <thead className="table-light">
                                    <tr>
                                        <th>Description</th>
                                        <th>QB Account</th>
                                        <th>Class</th>
                                        <th className="text-end">Amount</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(qboPreview.lines || []).map((l, i) => (
                                        <tr key={i}>
                                            <td>{l.description}</td>
                                            <td>{l.accountName || <span className="text-danger">— not linked —</span>}</td>
                                            <td>{l.className || '—'}</td>
                                            <td className="text-end">${money(l.amount)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot>
                                    <tr>
                                        <td colSpan="3" className="text-end fw-bold">Total</td>
                                        <td className="text-end fw-bold">${money(qboPreview.total)}</td>
                                    </tr>
                                </tfoot>
                            </Table>
                        </>
                    )}
                    <div className="d-flex justify-content-end gap-2">
                        {qboBlocked ? (
                            <Button color="primary" onClick={() => setQboPreviewOpen(false)}>Close</Button>
                        ) : (
                            <>
                                <Button color="secondary" onClick={() => setQboPreviewOpen(false)} disabled={posting}>Cancel</Button>
                                <Button color="primary" onClick={confirmPush} disabled={posting}>
                                    {posting ? <><Spinner size="sm" /> Posting...</> : "Post to QuickBooks"}
                                </Button>
                            </>
                        )}
                    </div>
                </ModalBody>
            </Modal>

            <Modal isOpen={!!selectedPaySheet} toggle={() => setSelectedPaySheet(null)}>
                <ModalHeader toggle={() => setSelectedPaySheet(null)}>Void Paysheet</ModalHeader>
                <ModalBody>
                    <FormGroup>
                        <Label>
                            This will void only the selected paysheet. The record will remain available in reports, and finance can generate a new version.
                        </Label>
                    </FormGroup>
                    <div className="d-flex justify-content-end gap-2">
                        <Button color="secondary" onClick={() => setSelectedPaySheet(null)} disabled={voiding}>Cancel</Button>
                        <Button color="warning" onClick={voidPaySheet} disabled={voiding}>
                            {voiding ? <><Spinner size="sm" /> Voiding...</> : "Void Paysheet"}
                        </Button>
                    </div>
                </ModalBody>
            </Modal>
        </Container>
    );
};

export default ApprovedPaySheetsReport;
